import { useState, useContext, useEffect } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { string, ref } from "yup";
import {
  Box,
  Grid,
  Stack,
  Button,
  Select,
  Dialog,
  Tooltip,
  MenuItem,
  TextField,
  Typography,
  FormControl,
  DialogContent,
  InputAdornment,
} from "@mui/material";
import { useMutation } from "@apollo/client";
//Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
//Import from project
import "../../Style/dialogstyle.scss";
import { CREATE_USER, UPDATE_USER } from "../../Schema/User";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import { useDeleteImageServer } from "../CropImage/DeleteImageServer";
import EmptyImage from "../../Assets/empty-image.png";
import CropImageFile from "../CropImage/CropImageFile";
import LoadingProgess from "../../Utils/LoadingProgress";

export default function UserForm({
  open,
  handleClose,
  setRefetch,
  editData,
  dialogTitle,
}) {
  // Change Language
  const { t, setAlert } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const mutationDelete = useDeleteImageServer();
  
  //hiden and show vibility =======================================
  const [show, setShow] = useState(false);
  const handleClick = () => setShow(!show);

  // =================================== Upload Image ====================================
  const [profileHook, setProfileHook] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [photoURL, setPhotoURL] = useState(null);
  const [openCrop, setOpenCrop] = useState(false);

  const [statusProgress, setStatusProgress] = useState(false);
  const [progress, setProgress] = useState(10);

  const handleUploadImage = (e) => {
    const imageFile = e.target.files[0];
    if (imageFile) {
      setImageFile(imageFile);
      setPhotoURL(URL.createObjectURL(imageFile));
      setOpenCrop(true);
    }
  };

  const handleCloseAction = () => {
    handleClose();
    if (profileHook) {
      // delete iamge from server ====================================================
      let splitSrc = profileHook?.split(":")[4];
      mutationDelete.mutate({
        storage: "sr_province_council",
        folder: "council_images",
        file: splitSrc.split("/")[0],
      });
    }
    setProfileHook("");
    setImageFile("");
  };

  // ================================= End upload Image ===============================

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: ({ createUser }) => {
      setLoading(false);
      if (createUser?.isSuccess === true) {
        setAlert(true, "success", createUser?.message);
        handleClose();
        resetForm();
        setRefetch();
        setProfileHook("");
        setImageFile("");
      } else {
        setAlert(true, "error", createUser?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  const [updateUser] = useMutation(UPDATE_USER, {
    onCompleted: ({ updateUser }) => {
      setLoading(false);
      if (updateUser?.isSuccess === true) {
        setAlert(true, "success", updateUser?.message);
        handleClose();
        resetForm();
        setRefetch();
        setProfileHook("");
        setImageFile("");
      } else {
        setAlert(true, "error", updateUser?.message);
      }
    },
    onError: ({ message }) => {
      setLoading(false);
      setAlert(true, "error", { messageKh: message, messageEn: message });
    },
  });

  //fromik
  const CheckValidation = Yup.object().shape({
    lastNameKh: Yup.string().required(t("required")),
    firstNameKh: Yup.string().required(t("required")),
    profile: Yup.string(),
    gender: Yup.string(),
    email: Yup.string()
      .required("Required")
      .test("email-or-phone", function (value) {
        if (!value) return false;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[0-9]{8,15}$/; // adjust to your rules

        if (emailRegex.test(value)) {
          return true; // valid email
        }

        if (phoneRegex.test(value)) {
          return true; // valid phone
        }

        // if it looks like it has @ but invalid email
        if (value.includes("@")) {
          return this.createError({ message: "Invalid email format" });
        }

        // else assume it's phone input but invalid
        return this.createError({ message: "Invalid phone number" });
      }),

    password:
      dialogTitle === "Create"
        ? Yup.string()
            .required("required!")
            .min(8, "Please input more then 8 charactor!")
        : Yup.string(),
    confirmPassword:
      dialogTitle === "Create"
        ? string()
            .required(t("required"))
            .oneOf([ref("password")], "Wrong password!")
        : Yup.string(),
    role: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      lastNameKh: "",
      firstNameKh: "",
      profile: "",
      gender: "Male",
      email: "",
      password: "",
      confirmPassword: "",
      role: "Admin",
    },

    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createUser({
          variables: {
            input: {
              lastNameKh: values?.lastNameKh,
              firstNameKh: values?.firstNameKh,
              profile: profileHook,
              gender: values?.gender,
              role: values?.role,
              email: values?.email,
              password: values?.password,
            },
          },
        });
      } else {
      
        updateUser({
          variables: {
            id: editData?._id,
            input: {
              lastNameKh: values?.lastNameKh,
              firstNameKh: values?.firstNameKh,
              profile: profileHook,
              gender: values?.gender,
              role: values?.role,
              email: values?.email,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    getFieldProps,
    values,
    resetForm,
  } = formik;

  useEffect(() => {
    if (editData) {
      setFieldValue("lastNameKh", editData?.lastNameKh);
      setFieldValue("firstNameKh", editData?.firstNameKh);
      setFieldValue("gender", editData?.gender);
      setFieldValue("email", editData?.email);
    }
  }, [editData, open]);

  return (
    <Dialog open={open} className="dialog-container" maxWidth="sm" fullWidth>
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_user")
            : t("modal_title_update_user")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleCloseAction}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={12}>
                {!openCrop ? (
                  <Box>
                    <Stack direction="row" justifyContent="center">
                      <Tooltip title="click to upload">
                        <Button component="label">
                          <TextField
                            className="text-field"
                            type="file"
                            id="image"
                            sx={{ display: "none" }}
                            onChange={handleUploadImage}
                          />
                          <img
                            className="avater-image"
                            src={
                              imageFile
                                ? URL.createObjectURL(imageFile)
                                : EmptyImage
                            }
                          />
                        </Button>
                      </Tooltip>
                    </Stack>
                  </Box>
                ) : (
                  <CropImageFile
                    setProgress={setProgress}
                    setStatusProgress={setStatusProgress}
                    openCrop={openCrop}
                    setOpenCrop={setOpenCrop}
                    photoURL={photoURL}
                    setPhotoURL={setPhotoURL}
                    setImageFile={setImageFile}
                    setProfileHook={setProfileHook}
                  />
                )}

                {statusProgress && progress < 100 ? (
                  <LoadingProgess
                    progress={progress}
                    setProgress={setProgress}
                  />
                ) : (
                  <Typography
                    className={t("class-name-field-title")}
                    sx={{ textAlign: "center", marginTop: "10px" }}
                  >
                    {t("thead_profile")}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_last_name")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_last_name")}
                  {...getFieldProps("lastNameKh")}
                  error={Boolean(touched.lastNameKh && errors.lastNameKh)}
                  helperText={touched.lastNameKh && errors.lastNameKh}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_first_name")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_first_name")}
                  {...getFieldProps("firstNameKh")}
                  error={Boolean(touched.firstNameKh && errors.firstNameKh)}
                  helperText={touched.firstNameKh && errors.firstNameKh}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_gender")}
                </Typography>
                <FormControl fullWidth className="search-field" size="small">
                  <Select
                    value={values?.gender}
                    onChange={(e) => setFieldValue("gender", e?.target.value)}
                  >
                    <MenuItem value={"Male"}>{t("thead_male")}</MenuItem>
                    <MenuItem value={"Female"}>{t("thead_female")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_role")}
                </Typography>
                <FormControl fullWidth className="search-field" size="small">
                  <Select
                    value={values?.role}
                    onChange={(e) => setFieldValue("role", e?.target.value)}
                  >
                    <MenuItem value={"SuperAdmin"}>Super Admin</MenuItem>
                    <MenuItem value={"Admin"}>Admin</MenuItem>
                    <MenuItem value={"Member"}>Member</MenuItem>
                    <MenuItem value={"Secretary"}>Secretary</MenuItem>
                    <MenuItem value={"Candidate"}>Candidate</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_email_or_phone")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  fullWidth
                  placeholder={t("thead_email_or_phone")}
                  {...getFieldProps("email")}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>

              {dialogTitle === "Create" && (
                <>
                  <Grid item xs={6}>
                    <Typography className={t("class-name-field-title")}>
                      {t("thead_password")}
                    </Typography>
                    <TextField
                      className="text-field"
                      fullWidth
                      size="small"
                      placeholder={t("thead_password")}
                      type={show ? "text" : "password"}
                      {...getFieldProps("password")}
                      error={Boolean(touched.password && errors.password)}
                      helperText={touched.password && errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="start"
                            onClick={handleClick}
                          >
                            {show ? (
                              <VisibilityIcon className="viewpw-icon" />
                            ) : (
                              <VisibilityOffIcon className="viewpw-icon" />
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography className={t("class-name-field-title")}>
                      {t("thead_confirm_password")}
                    </Typography>
                    <TextField
                      className="text-field"
                      fullWidth
                      size="small"
                      placeholder={t("thead_confirm_password")}
                      type={show ? "text" : "password"}
                      {...getFieldProps("confirmPassword")}
                      error={Boolean(
                        touched.confirmPassword && errors.confirmPassword
                      )}
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    className={t("class-btn-create")}
                    fullWidth
                    onClick={handleSubmit}
                    disabled={statusProgress && progress < 100 ? true : false}
                  >
                    {dialogTitle === "Create" ? t("create") : t("update")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
